import './Reels.css';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Heart, Send, Plus } from 'lucide-react';
import trackEvent from '../utils/analytics';
import { apiCall } from '../utils/api';
import CreatePost from './CreatePost';

const AVATAR_COLORS = [
  '#ff6b6b',
  '#5efce8',
  '#ff9a8b',
  '#a1c4fd',
  '#f6d365',
  '#84fab0',
  '#ffecd2',
  '#cfd9df',
];

const Reels = ({ onOpenProfile }) => {
  const [liked, setLiked] = useState({});
  const [pendingConnections, setPendingConnections] = useState({});
  const [items, setItems] = useState([]);
  const [expandedText, setExpandedText] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastRecordId, setLastRecordId] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [posterCache, setPosterCache] = useState({});
  const [showCreatePost, setShowCreatePost] = useState(false);
  const videoRefs = useRef({});
  const [priorityFolder, setPriorityFolder] = useState(null);
  const [priorityBase, setPriorityBase] = useState(null);
  const priorityLikesRef = useRef({});

  // Intersection Observer for video autoplay
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.6, // Video must be 60% visible to autoplay
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting) {
          // Play video when it comes into view
          video.play().catch(() => {
            // Autoplay was prevented, ignore silently
          });
        } else {
          // Pause video when it goes out of view
          video.pause();
        }
      });
    }, options);

    // Observe all video elements - slight delay to let DOM render
    const observeTimer = setTimeout(() => {
      Object.values(videoRefs.current).forEach((video) => {
        if (video) {
          observer.observe(video);
        }
      });
    }, 100);

    return () => {
      clearTimeout(observeTimer);
      observer.disconnect();
    };
  }, [items]); // Re-run when items change
  const ANON_NAMES = ['CA Rahul', 'Raju', 'CA Guru'];

  const generateAnonUserId = () => `anon-${Math.random().toString(36).slice(2, 9)}`;

  const pickAnonName = () => ANON_NAMES[Math.floor(Math.random() * ANON_NAMES.length)];

  const generateRandomLikes = () => Math.floor(Math.random() * 101);

  const getFolder = (item) => item?.feed?.folder || item?.folder;

  const ensureUserIdAndName = (item) => {
    const updates = {};
    if (!item._id) {
      updates._id = `priority-${Math.random().toString(36).slice(2, 11)}`;
    }
    if (!item.user_id) {
      updates.user_id = generateAnonUserId();
    }
    if (!item.user_name) {
      updates.user_name = pickAnonName();
    }
    if (item.likes === undefined || item.likes === null) {
      updates.likes = generateRandomLikes();
    }
    return Object.keys(updates).length ? { ...item, ...updates } : item;
  };

  const fetchFeed = useCallback(async (lastId = null) => {
    try {
      if (lastId) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      let url = '/feed?limit=20';
      if (lastId) {
        url += `&last_record_id=${lastId}`;
      }

      const response = await apiCall.get(url);
      let feedData = response.data || response || [];
      if (Array.isArray(feedData)) {
        feedData = feedData.map(ensureUserIdAndName);
      }
      const priority = response.priority_folder || response.data?.priority_folder || null;
      setPriorityFolder(priority);
      if (priority) {
        const parts = priority.split('/');
        const base =
          parts.length > 1 ? parts.slice(0, parts.length - 1).join('/') : priority;
        setPriorityBase(base);
      } else {
        setPriorityBase(null);
      }

      if (Array.isArray(feedData)) {
        if (lastId) {
          setItems(prev => [...prev, ...feedData]);
        } else {
          setItems(feedData);
        }

        // Update last record id for pagination
        if (feedData.length > 0) {
          setLastRecordId(feedData[feedData.length - 1]._id);
        }

        // Check if there's more data
        setHasMore(feedData.length === 20);
      }
    } catch (error) {
      console.error('Error fetching feed:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  useEffect(() => {
    priorityLikesRef.current = {};
  }, [items, priorityFolder, priorityBase]);

  const loadMore = () => {
    if (!loadingMore && hasMore && lastRecordId) {
      fetchFeed(lastRecordId);
    }
  };

  const sortedItems = useMemo(() => {
    const sortByTimeDesc = (list) => {
      return [...list].sort((a, b) => {
        const aTime = a?.c || 0;
        const bTime = b?.c || 0;
        return bTime - aTime;
      });
    };

    const shuffleAvoidSameUser = (list) => {
      const result = [];
      const remaining = [...list];

      while (remaining.length > 0) {
        const lastUserId = result.length > 0 ? result[result.length - 1]?.user_id : null;

        let nextIndex = remaining.findIndex(item => item.user_id !== lastUserId);
        if (nextIndex === -1) {
          nextIndex = 0;
        }

        result.push(remaining[nextIndex]);
        remaining.splice(nextIndex, 1);
      }

      return result;
    };

    const priorityItems = priorityBase
      ? sortByTimeDesc(
          items.filter((item) => {
            const folder = getFolder(item);
            return (
              folder === priorityFolder ||
              (folder && folder.startsWith(`${priorityBase}/`))
            );
          })
        )
      : [];
    const otherItems = sortByTimeDesc(items.filter(item => !priorityFolder || getFolder(item) !== priorityFolder));
    const shuffledOthers = shuffleAvoidSameUser(otherItems);

    return [...priorityItems, ...shuffledOthers];
  }, [items, priorityFolder, priorityBase]);

  const captureFirstFrame = useCallback((itemId, videoElement) => {
    if (!videoElement) return;

    setPosterCache((prev) => {
      if (prev[itemId]) return prev;

      try {
        const canvas = document.createElement('canvas');
        canvas.width = videoElement.videoWidth || videoElement.clientWidth;
        canvas.height = videoElement.videoHeight || videoElement.clientHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/png');
        return { ...prev, [itemId]: dataUrl };
      } catch (error) {
        return prev;
      }
    });
  }, []);

  const renderMedia = (item) => {
    const imageUrl = item?.feed?.image_url;
    if (!imageUrl) return null;

    // Check if it's a video based on extension
    const isVideo = imageUrl.match(/\.(mp4|webm|mov)$/i);

    if (isVideo) {
      const posterUrl = posterCache[item._id] || item.feed?.thumbnail_url || undefined;

      return (
        <div className="feed-media">
          <video
            ref={(el) => {
              if (el) {
                videoRefs.current[item._id] = el;
              }
            }}
            className="feed-media-video"
            src={imageUrl}
            controls
            playsInline
            muted
            loop
            preload="metadata"
            aria-label="Post video"
            poster={posterUrl}
            onLoadedData={(event) => {
              if (!posterCache[item._id]) {
                captureFirstFrame(item._id, event.target);
              }
            }}
            onPlay={() => {
              try {
                trackEvent('feed_video_played', { post_id: item._id });
              } catch (e) {
                // ignore analytics failures
              }
            }}
          />
        </div>
      );
    }

    return (
      <div className="feed-media">
        <img
          className="feed-media-image"
          src={imageUrl}
          alt="Post image"
          loading="lazy"
        />
      </div>
    );
  };

  // Get user initials for avatar fallback
  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'U';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  };

  const getAuthorDisplayName = (item) => {
    const onboardingChoices =
      item.onboarding_details?.onboarding_choices ||
      item.onboarding_details ||
      item.onboarding;
    const onboardingName = onboardingChoices?.name;
    if (onboardingName && onboardingName.trim()) {
      return onboardingName.trim();
    }
    if (item.user_name && item.user_name.trim()) {
      return item.user_name.trim();
    }
    return 'Anonymous';
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp * 1000);
      const now = new Date();
      const diff = Math.floor((now - date) / 1000);

      if (diff < 60) return 'Just now';
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
      
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (e) {
      return '';
    }
  };

  // Get subtitle from onboarding details
  const getSubtitle = (item) => {
    const details = item.onboarding_details;
    if (!details) return '';
    
    const parts = [];
    if (details.qualification) parts.push(details.qualification);
    if (details.city) parts.push(details.city);
    if (details.total_experience_years) parts.push(`${details.total_experience_years} yrs exp`);
    
    return parts.join(' • ');
  };

  const toggleLike = (id) => {
    setLiked((prev) => {
      const next = !prev[id];
      try {
        trackEvent('feed_like_toggled', { post_id: id, liked: next });
      } catch (e) {
        // ignore analytics failures
      }
      return { ...prev, [id]: next };
    });
  };

  const handleShare = async (id, text) => {
    const shareUrl = window.location.origin + window.location.pathname + '?utm_source=feed_share';
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Post', text: text || 'Check this out!', url: shareUrl });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(`${text || 'Check this out!'}\n${shareUrl}`);
      }
      try {
        trackEvent('feed_shared', { post_id: id });
      } catch (e) {
        // ignore analytics failures
      }
    } catch (e) {
      // ignore share failures
    }
  };

  const handleConnect = (id) => {
    setPendingConnections((prev) => ({
      ...prev,
      [id]: true,
    }));
  };

  const handlePostCreated = useCallback((newPost) => {
    setItems(prev => {
      if (prev.some(item => item._id === newPost?._id)) {
        return prev;
      }
      return [newPost, ...prev];
    });
    try {
      trackEvent('feed_post_created', { post_id: newPost?._id });
    } catch (e) {
      // ignore analytics failures
    }
    fetchFeed();
  }, [fetchFeed]);

  if (loading) {
    return null;
  }

  return (
    <div className="feed-screen">
      <div className="feed-topbar" role="banner">
        <div className="feed-title">Feed</div>
        {onOpenProfile && (
          <button 
            className="feed-profile-btn"
            onClick={onOpenProfile}
            aria-label="Open Profile"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </button>
        )}
      </div>

      <div className="feed-scroll" role="main" aria-label="Feed">
        <div className="feed-list">
          {sortedItems.length === 0 ? (
            <div className="feed-empty">
              <p>No posts yet</p>
            </div>
          ) : (
          sortedItems.map((item, idx) => {
              const colorIndex = idx % AVATAR_COLORS.length;
              const avatarColor = AVATAR_COLORS[colorIndex];
              const isLiked = liked[item._id] === true;
            const folder = getFolder(item);
            const isPriorityFlagItem =
              Boolean(item.priority_video) || Boolean(item.feed?.priority);
            const isPriorityFolderItem =
              Boolean(
                priorityFolder &&
                  folder &&
                  (folder === priorityFolder ||
                    (priorityBase && folder.startsWith(`${priorityBase}/`)))
              );
            const isPriorityItem = isPriorityFlagItem || isPriorityFolderItem;

            if (isPriorityItem && !priorityLikesRef.current[item._id]) {
              priorityLikesRef.current[item._id] = 100 + Math.floor(Math.random() * 51);
            }

            const baseLikes = isPriorityItem
              ? priorityLikesRef.current[item._id]
              : item.likes || 0;

            const likeCount = baseLikes + (isLiked ? 1 : 0);

              return (
                <article className="feed-card" key={item._id}>
                  <header className="feed-card-header">
                    <div className="feed-author">
                      <div
                        className="feed-avatar-initials"
                        style={{ background: avatarColor }}
                      >
                        {getInitials(getAuthorDisplayName(item))}
                      </div>
                      <div className="feed-author-meta">
                        <div className="feed-author-name-row">
                          <span className="feed-author-name">{getAuthorDisplayName(item)}</span>
                        </div>
                        <div className="feed-author-subtitle">{getSubtitle(item)}</div>
                      </div>
                    </div>
                    <button
                      className={`connect-btn ${pendingConnections[item._id] ? 'pending' : ''}`}
                      type="button"
                      onClick={() => handleConnect(item._id)}
                    >
                      {pendingConnections[item._id] ? 'Pending' : 'Connect'}
                    </button>
                  </header>

                  {renderMedia(item)}

                  <div className={`feed-text ${expandedText[item._id] ? 'expanded' : 'collapsed'}`}>
                    {(item.feed?.text || '').split('\n').map((line, idx) => (
                      <p className="feed-paragraph" key={`${item._id}-p-${idx}`}>
                        {line || '\u00A0'}
                      </p>
                    ))}
                  </div>
                  {item.feed?.text && (item.feed.text.length > 220 || item.feed.text.split('\n').length > 3) && (
                    <button
                      type="button"
                      className="feed-text-toggle"
                      onClick={() =>
                        setExpandedText(prev => ({
                          ...prev,
                          [item._id]: !prev[item._id],
                        }))
                      }
                    >
                      {expandedText[item._id] ? 'Show less' : 'Show more'}
                    </button>
                  )}

                  <div className="feed-actions" role="group" aria-label="Post actions">
                    <button
                      type="button"
                      className={`feed-action ${isLiked ? 'liked' : ''}`}
                      onClick={() => toggleLike(item._id)}
                      aria-pressed={isLiked}
                      aria-label={isLiked ? 'Unlike' : 'Like'}
                    >
                      <Heart size={20} />
                      <span className="feed-action-count">{likeCount}</span>
                    </button>

                    <button
                      type="button"
                      className="feed-action"
                      onClick={() => handleShare(item._id, item.feed?.text)}
                      aria-label="Share"
                    >
                      <Send size={20} />
                    </button>
                  </div>

                  <div className="feed-time">{formatTime(item.c)}</div>
                </article>
              );
            })
          )}

          {hasMore && items.length > 0 && (
            <button 
              className="feed-load-more" 
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading...' : 'Load more'}
            </button>
          )}
        </div>
      </div>
      
      {/* Create Post FAB */}
      <button 
        className="create-post-fab"
        onClick={() => setShowCreatePost(true)}
        aria-label="Create post"
      >
        <Plus size={28} />
      </button>
      
      {/* Create Post Modal */}
      <CreatePost 
        isOpen={showCreatePost}
        onClose={() => setShowCreatePost(false)}
        onPostCreated={handlePostCreated}
      />
    </div>
  );
};

export default Reels;
